import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    ReactNode,
    useRef,
} from "react";
import { supabase } from "../helper/MessagingAuth";
import { MessageAuthUser } from "../helper/MessagingAuth";
import { useAuth } from "./AuthContext";
import { showSuccess } from "../components/common/ToastNotification";

type UnreadCounts = { [roomId: string]: number };

type AuthContextType = {
    user: any;
    loading: boolean;
    unreadCounts: UnreadCounts;
    directChatRooms: Record<string, any>;
    incrementUnread: (roomId: string) => void;
    resetUnread: (roomId: string) => void;
    loginWithParams: (params: any, token?: string) => Promise<any>;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

//* UUID validation helper
const isValidUUID = (id: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

export const MessageAuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({});
    const [directChatRooms, setDirectChatRooms] = useState<Record<string, any>>({});
    const { userInfo } = useAuth();
    const authAttemptRef = useRef<string | null>(null);
    const myGroupRoomIdsRef = useRef<Set<string>>(new Set());

    //* Unread Helpers
    const incrementUnread = useCallback((roomId: string) => {
        setUnreadCounts(prev => ({
            ...prev,
            [roomId]: (prev[roomId] || 0) + 1,
        }));
    }, []);

    const resetUnread = useCallback((roomId: string) => {
        setUnreadCounts(prev => ({
            ...prev,
            [roomId]: 0,
        }));
    }, []);

    //* Load initial unread counts
    const loadInitialCounts = useCallback(async () => {
        if (!userInfo || !user?.id) return;

        if (!isValidUUID(user.id)) {
            console.warn("⚠️ user.id is not a valid UUID, skipping unread count load:", user.id);
            return;
        }

        try {
            const counts: UnreadCounts = {};
            const activeAgencyId = Number(userInfo.agency?.id || 1122);

            // ───────────────── DIRECT CHATS ─────────────────
            // Fetch direct chat participations
            const { data: directParticipations, error: directPartError } = await supabase
                .from("chat_participants")
                .select("room_id")
                .eq("user_id", user.id);

            if (directPartError) {
                console.error("Error fetching direct chat participants:", directPartError);
            } else if (directParticipations?.length) {
                const directRoomIds = directParticipations.map((p: any) => p.room_id);

                // Fetch all participants of these direct rooms to identify the other user
                const { data: allParticipants, error: allPartError } = await supabase
                    .from("chat_participants")
                    .select("room_id, user_id")
                    .in("room_id", directRoomIds);

                if (allPartError) {
                    console.error("Error fetching direct room participants:", allPartError);
                } else if (allParticipants?.length) {
                    // Collect other user IDs
                    const otherUserIds = allParticipants
                        .filter((p: any) => p.user_id !== user.id)
                        .map((p: any) => p.user_id);

                    // Fetch profiles of other users to verify their agency
                    const { data: otherProfiles, error: profilesError } = await supabase
                        .from("user_info")
                        .select("id, agency_id")
                        .in("id", otherUserIds);

                    if (profilesError) {
                        console.error("Error fetching other user profiles:", profilesError);
                    } else {
                        // Filter user IDs that belong to the active agency
                        const validOtherUserIds = new Set<string>();
                        otherProfiles?.forEach((profile: any) => {
                            const agencies = Array.isArray(profile.agency_id)
                                ? profile.agency_id.map((id: any) => Number(id))
                                : [Number(profile.agency_id)];

                            if (agencies.includes(activeAgencyId)) {
                                validOtherUserIds.add(profile.id);
                            }
                        });

                        // Filter directRoomIds to only those with valid participants
                        const filteredDirectRoomIds = directRoomIds.filter(roomId => {
                            const roomParts = allParticipants.filter((p: any) => p.room_id === roomId);
                            const otherId = roomParts.find((p: any) => p.user_id !== user.id)?.user_id;
                            return otherId && validOtherUserIds.has(otherId);
                        });

                        if (filteredDirectRoomIds.length) {
                            const now = new Date();
                            const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString();
                            // Fetch messages from these filtered rooms
                            const { data: directMessages, error: directMsgError } = await supabase
                                .from("messages")
                                .select("id, room_id")
                                .in("room_id", filteredDirectRoomIds)
                                .neq("sender_id", user.id)
                                .gte("created_at", sevenDaysAgo);

                            if (directMsgError) {
                                console.error("Error fetching direct messages:", directMsgError);
                            } else if (directMessages?.length) {
                                const messageIds = directMessages.map((m: any) => m.id);

                                // Fetch read receipts
                                const { data: readMsgs, error: readError } = await supabase
                                    .from("message_reads")
                                    .select("message_id")
                                    .in("message_id", messageIds)
                                    .eq("user_id", user.id);

                                if (readError) {
                                    console.error("Error fetching direct message reads:", readError);
                                } else {
                                    const readSet = new Set(readMsgs?.map((r: any) => r.message_id));

                                    directMessages.forEach((msg: any) => {
                                        if (!readSet.has(msg.id)) {
                                            counts[msg.room_id] = (counts[msg.room_id] || 0) + 1;
                                        }
                                    });
                                }
                            }
                        }
                    }
                }
            }

            // ───────────────── GROUP CHATS ─────────────────
            // Fetch group chat participations
            const { data: groupParticipations, error: groupPartError } = await supabase
                .from("group_chat_participants")
                .select("room_id")
                .eq("user_id", user.id);

            if (groupPartError) {
                console.error("Error fetching group chat participants:", groupPartError);
            } else if (groupParticipations?.length) {
                const groupRoomIds = groupParticipations.map((p: any) => p.room_id);

                // Fetch all participants of these groups to check their agency
                const { data: allGroupParticipants, error: allGPartError } = await supabase
                    .from("group_chat_participants")
                    .select("room_id, user_id")
                    .in("room_id", groupRoomIds);

                if (allGPartError) {
                    console.error("Error fetching group participants:", allGPartError);
                } else if (allGroupParticipants?.length) {
                    const groupUserIds = allGroupParticipants.map((p: any) => p.user_id);

                    // Fetch user profiles to check agencies
                    const { data: groupProfiles, error: gProfilesError } = await supabase
                        .from("user_info")
                        .select("id, agency_id")
                        .in("id", groupUserIds);

                    if (gProfilesError) {
                        console.error("Error fetching group user profiles:", gProfilesError);
                    } else {
                        const validGroupUserIds = new Set<string>();
                        groupProfiles?.forEach((profile: any) => {
                            const agencies = Array.isArray(profile.agency_id)
                                ? profile.agency_id.map((id: any) => Number(id))
                                : [Number(profile.agency_id)];

                            if (agencies.includes(activeAgencyId)) {
                                validGroupUserIds.add(profile.id);
                            }
                        });

                        // Filter groupRoomIds to only rooms where at least one other participant shares the active agency
                        const filteredGroupRoomIds = groupRoomIds.filter(roomId => {
                            const roomParts = allGroupParticipants.filter((p: any) => p.room_id === roomId);
                            return roomParts.some((p: any) => p.user_id !== user.id && validGroupUserIds.has(p.user_id));
                        });

                        //* Keep this in sync so the realtime group_messages listener
                        //* knows exactly which rooms belong to this user.
                        myGroupRoomIdsRef.current = new Set(filteredGroupRoomIds);

                        if (filteredGroupRoomIds.length) {
                            const now = new Date();
                            const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString();
                            // Fetch group messages
                            const { data: groupMessages, error: groupMsgError } = await supabase
                                .from("group_messages")
                                .select("id, room_id")
                                .in("room_id", filteredGroupRoomIds)
                                .neq("sender_id", user.id)
                                .gte("created_at", sevenDaysAgo);

                            if (groupMsgError) {
                                console.error("Error fetching group messages:", groupMsgError);
                            } else if (groupMessages?.length) {
                                const messageIds = groupMessages.map((m: any) => m.id);

                                // Fetch read receipts
                                const { data: readGroupMsgs, error: readGroupError } = await supabase
                                    .from("group_message_reads")
                                    .select("message_id")
                                    .in("message_id", messageIds)
                                    .eq("user_id", user.id);

                                if (readGroupError) {
                                    console.error("Error fetching group message reads:", readGroupError);
                                } else {
                                    const readSet = new Set(readGroupMsgs?.map((r: any) => r.message_id));

                                    groupMessages.forEach((msg: any) => {
                                        if (!readSet.has(msg.id)) {
                                            counts[msg.room_id] = (counts[msg.room_id] || 0) + 1;
                                        }
                                    });
                                }
                            }
                        } else {
                            // No group rooms — make sure stale IDs don't linger
                            myGroupRoomIdsRef.current = new Set();
                        }
                    }
                }
            } else {
                // No group participations at all
                myGroupRoomIdsRef.current = new Set();
            }

            setUnreadCounts(counts);
        } catch (err) {
            console.error("Error loading unread counts:", err);
        }
    }, [user?.id, userInfo]);

    //* Auth Actions
    const loginWithParams = useCallback(async (params: any, token?: string) => {
        setLoading(true);
        const profile = await MessageAuthUser(params, token);
        if (profile) {
            if (!isValidUUID(profile.id)) {
                console.warn("⚠️ loginWithParams: returned profile.id is not a valid UUID:", profile.id);
            }
            setUser(profile);
        }
        setLoading(false);
        return profile;
    }, []);

    const logout = useCallback(async () => {
        await supabase.auth.signOut();
        setUser(null);
        setUnreadCounts({});
        setDirectChatRooms({});
        myGroupRoomIdsRef.current = new Set();
    }, []);

    //* Sync user session automatically from main AuthContext
    useEffect(() => {
        const syncUser = async () => {
            if (!userInfo) {
                if (user || authAttemptRef.current !== null) {
                    await logout();
                }
                authAttemptRef.current = null;
                setLoading(false);
                return;
            }

            if (user) {
                setLoading(false);
                return; // Already synchronized
            }

            if (authAttemptRef.current === userInfo.uuid) {
                return; // Already attempted/attempting login for this user
            }
            authAttemptRef.current = userInfo.uuid;

            // Map userInfo to matching Supabase params
            const params = {
                agency_id: userInfo.agency?.id || 1122,
                email_id: userInfo.email_id || userInfo.email,
                mobile_number: userInfo.contact_no,
                role: userInfo.employee_roles?.[0]?.role_name || userInfo.access_type,
                sso_id: userInfo.uuid,
                first_name: userInfo.first_name,
                last_name: userInfo.last_name,
                uuid: userInfo.uuid,
            };

            if (params.sso_id && params.email_id) {
                try {
                    setLoading(true);
                    await loginWithParams(params);
                } catch (err) {
                    console.error("❌ Auto login to messaging failed:", err);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        syncUser();
    }, [userInfo, user, loginWithParams, logout]);

    //* Load initial unread counts when user is established
    useEffect(() => {
        if (userInfo && user?.id) {
            loadInitialCounts();
        }
    }, [userInfo, user?.id, loadInitialCounts]);

    //* Realtime Listener for messages and mark-as-read updates
    useEffect(() => {
        if (!userInfo || !user?.id) return;

        if (!isValidUUID(user.id)) {
            console.warn("⚠️ user.id is not a valid UUID, skipping realtime subscription:", user.id);
            return;
        }

        const channel = supabase
            .channel(`realtime-messages-${user.id}`)
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "messages",
                    filter: `receiver_id=eq.${user.id}`,
                },
                (payload: any) => {
                    const message = payload.new;
                    if (message.sender_id === user.id) return;
                    if (window.location.pathname !== "/message") {
                        showSuccess("New message received!");
                    }
                    loadInitialCounts();
                }
            )
            .on(
                "postgres_changes",
                { event: "INSERT", schema: "public", table: "group_messages" },
                (payload: any) => {
                    const message = payload.new;
                    if (message.sender_id === user.id) return;
                    if (!myGroupRoomIdsRef.current.has(message.room_id)) return;
                    if (window.location.pathname !== "/message") {
                        showSuccess("New message received!");
                    }
                    loadInitialCounts();
                }
            )
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "message_reads",
                    filter: `user_id=eq.${user.id}`,
                },
                () => {
                    loadInitialCounts();
                }
            )
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "group_message_reads",
                    filter: `user_id=eq.${user.id}`,
                },
                () => {
                    loadInitialCounts();
                }
            )
            .subscribe((status: any) =>
                console.log("🔌 Realtime status:", status)
            );

        return () => {
            supabase.removeChannel(channel);
        };
    }, [userInfo, user?.id, loadInitialCounts]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                unreadCounts,
                directChatRooms,
                incrementUnread,
                resetUnread,
                loginWithParams,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useMessageAuth = () => {
    const context = useContext(AuthContext);
    if (!context)
        throw new Error(
            "useMessageAuth must be used inside MessageAuthProvider"
        );
    return context;
};